import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinalCta from "@/components/FinalCta";
import AcademyCityCta from "@/components/AcademyCityCta";
import SolarMath, { computeMath } from "@/components/SolarMath";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { findPost } from "@/lib/blog";
import NotFound from "@/pages/NotFound";

const money = (n) => `$${n.toLocaleString("en-US")}`;

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);
  const { openAudit } = useSite();
  const [jobs, setJobs] = useState([]);

  const isCareers = post?.special === "careers";
  useEffect(() => {
    if (!isCareers) return;
    api.get("/careers-jobs").then((r) => setJobs(r.data.jobs)).catch(() => {});
  }, [isCareers]);

  useEffect(() => {
    if (!post || isCareers) return undefined;
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.id = "blog-jsonld";
    node.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: `Commercial and home solar in ${post.city}, FL: costs, incentives and the Powerwall question`,
      datePublished: "2026-10-21",
      author: { "@type": "Organization", name: "Solar Black Belt" },
      publisher: { "@type": "Organization", name: "Solar Black Belt", url: "https://commercialsolarroi.com" },
      about: { "@type": "City", name: post.city, addressRegion: "FL" },
    });
    document.head.appendChild(node);
    return () => {
      document.getElementById("blog-jsonld")?.remove();
    };
  }, [post]);

  if (!post) return <NotFound />;
  if (isCareers) {
    return (
      <>
        <Seo
          title="Now hiring — every solar position is open | Solar Black Belt Careers"
          description="All positions open in Florida: solar PV installer, solar electrician, electrical apprentice, metal roofing technician, HVAC technician and solar service technician. Apply with resume upload — every application reaches the same inbox."
        />
        <section className="page-hero">
          <div className="wrap">
            <span className="eyebrow">CAREERS · ALL POSITIONS OPEN</span>
            <h1 data-testid="blog-post-heading">Now hiring: every solar position is open.</h1>
            <p className="lead">{post.blurb}</p>
            <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
              <Link className="btn lime" to="/careers" data-testid="careers-apply-cta">
                See the open positions &amp; apply <span>↗</span>
              </Link>
            </div>
          </div>
        </section>
        <section className="section" style={{ paddingTop: 40 }}>
          <div className="wrap">
            <span className="eyebrow">OPEN ROLES</span>
            <h2 style={{ fontSize: 24, margin: "8px 0 14px" }}>{jobs.length} positions, statewide</h2>
            <div className="blog-grid" data-testid="careers-post-grid">
              {jobs.map((j) => (
                <article className="blog-card" key={j.id} data-testid={`careers-post-${j.id}`}>
                  <h3>{j.title}</h3>
                  <span className="report-tag">{j.type}</span>
                  <p>{j.blurb}</p>
                  <Link className="micro" to="/careers" style={{ fontWeight: 800, color: "var(--teal)" }}>
                    Description &amp; application →
                  </Link>
                </article>
              ))}
            </div>
            <p className="micro">
              Applications go to the same inbox as every lead on this site — one team, one inbox, one reference number per
              application. Resumes are stored privately and reviewed by humans.
            </p>
          </div>
        </section>
        <FinalCta title={["Not job hunting?", "Start with your roof."]} service="commercial" context="Careers blog quote request" />
      </>
    );
  }
  const m = computeMath(post.exampleBill);
  const faqs = [
    [`How much does commercial solar cost in ${post.city}?`, `Typical Florida commercial systems run about $2.30–$3.00 per watt installed. The ${m.kw.toLocaleString("en-US")} kW example worked out above prices around ${money(m.costLow)}–${money(m.costHigh)} — your roof, tariff and bills set the real number, which is what a bill-based review confirms.`],
    [`Is ${post.city} good for solar?`, `The sun is the easy part — Florida cities like ${post.city} have steady production all year. The economics come from your usage pattern, your utility (${post.utilities.split(".")[0]}) and your roof, not from a statewide average.`],
    [`Would Tesla Powerwall make sense in ${post.city}?`, `Storage enters the conversation when your building has short, expensive demand peaks or loads that must survive an outage — both worth studying in ${post.city}'s storm season. The right size comes from your utility's interval data or your bills.`],
    [`Do I need to own the building to start?`, `Owners move fastest, but tenants can start the review too — permission needs are covered in step one, and the math works the same either way.`],
  ];

  return (
    <>
      <Seo
        title={`Commercial & Home Solar in ${post.city}, FL — Costs, Incentives & Powerwall | Solar Black Belt`}
        description={`The full solar math for ${post.city}, ${post.county}: what your bill implies, installed cost ranges, payback scenarios, Tesla Powerwall fit, and how commercial or home solar projects start. ${post.utilities}`}
      />
      <section className="page-hero">
        <div className="wrap">
          <p className="micro" style={{ marginBottom: 14 }}>
            <Link to="/">Home</Link> · <Link to="/blog">Blog</Link> · {post.city}
          </p>
          <span className="eyebrow">{post.region.toUpperCase()} · {post.county.toUpperCase()}</span>
          <h1 data-testid="blog-post-heading">
            Solar in {post.city}: costs, incentives
            <br />
            &amp; the Powerwall question.
          </h1>
          <p className="lead">{post.blurb}</p>
          <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
            <button className="btn lime" data-testid="blog-upload-cta" onClick={() => openAudit()}>
              Upload my electric bill <span>↗</span>
            </button>
            <Link className="btn light" to="/#roi" data-testid="blog-calculator-link">
              Try the calculator first ↓
            </Link>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <SolarMath post={post} />

          <section style={{ marginTop: 34 }} data-testid="blog-powerwall">
            <span className="eyebrow">TESLA POWERWALL</span>
            <h2>Where Powerwall fits in {post.city}</h2>
            <p className="muted" style={{ fontSize: 13.5, maxWidth: 720, lineHeight: 1.75 }}>
              Solar produces; Powerwall moves energy in time. In {post.city}, that usually matters for two reasons: short
              demand peaks that set expensive charges, and storm season — when a correctly configured system can keep the loads
              you choose running and recharge from the sun the next day. Sizing is a power question (how much at once) and an
              energy question (how long for), and your bill or interval data answers both.
            </p>
            <ul className="benefitlist">
              <li>Backup for the loads you choose — refrigeration, servers, gates, lighting — not the whole building by default.</li>
              <li>Peak shaving for the hot-afternoon spikes that drive demand charges.</li>
              <li>Storm-season resilience with automatic islanding and next-day solar recharge.</li>
            </ul>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 16 }}>
              <Link className="btn light" to="/services/powerwall" data-testid="blog-powerwall-link">
                Explore Tesla Powerwall ↗
              </Link>
              <Link className="btn light" to="/#storage">
                Compare storage options ↓
              </Link>
            </div>
          </section>

          <section style={{ marginTop: 34 }}>
            <span className="eyebrow">COMMERCIAL OR HOME</span>
            <h2>Two ways to start in {post.city}</h2>
            <div className="funnel-grid">
              <article className="blog-card" data-testid="blog-funnel-commercial">
                <span className="eyebrow">FOR BUSINESS OWNERS</span>
                <h3>Commercial solar &amp; storage</h3>
                <p>
                  Upload one bill — or up to twelve months. The project team reviews usage, demand charges, sizing assumptions,
                  incentives and payback manually, then follows up by phone or email with next steps.
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                  <button className="btn" onClick={() => openAudit()}>Upload my bill <span>↗</span></button>
                  <Link className="btn light" to="/services/commercial">See commercial solar</Link>
                </div>
              </article>
              <article className="blog-card" data-testid="blog-funnel-home">
                <span className="eyebrow">FOR HOMEOWNERS</span>
                <h3>Home solar &amp; Powerwall</h3>
                <p>
                  Your home can run on the same honest math — sizing from your bill, Tesla Powerwall for outages, and roofing
                  handled by the same licensed crews so penetrations are warrantied together.
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                  <Link className="btn" to="/homeowners">Start my home quote <span>↗</span></Link>
                  <Link className="btn light" to="/services/home">See home solar</Link>
                </div>
              </article>
            </div>
          </section>

          <section style={{ marginTop: 34 }} data-testid="blog-faq">
            <span className="eyebrow">{post.city.toUpperCase()} QUESTIONS</span>
            <h2>Quick answers for {post.city}</h2>
            <div>
              {faqs.map(([q, a]) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
            <p className="micro">
              Serving {post.city}, {post.county} — licensed Florida crews (Roofing CCC1335978 · Electrical EC13013993). Utility
              context: {post.utilities}
            </p>
          </section>
        </div>
      </section>

      <AcademyCityCta city={{ name: post.city }} />
      <FinalCta title={[`Let's talk`, `${post.city}.`]} service="commercial" context={`${post.city} blog quote request`} />
    </>
  );
}
